import React, { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import { useLazyGetSponserBusinessesQuery, useGetPageContentQuery } from "../api/businessApi";
import SEO from "../components/SEO";
import {
  HiOutlineOfficeBuilding,
  HiOutlineCalendar,
  HiOutlineUsers,
  HiOutlineSupport,
  HiOutlineTrendingUp,
  HiOutlineUserGroup,
  HiOutlineCurrencyDollar,
  HiOutlineDesktopComputer,
  HiOutlineBriefcase,
} from "react-icons/hi";

const Home = () => {
  const [getSponserBusinesses, { data, isSuccess, isError, error, isLoading }] =
    useLazyGetSponserBusinessesQuery();
  const [sponserBusinesses, setSponserBusinesses] = useState([]);

  // Fetch page content from API
  const { data: pageData } = useGetPageContentQuery("home");

  // Helper to get section content with fallback.
  // Merges per-field rather than replacing wholesale: a stored section that
  // defines only some fields (they commonly do — the schemas are narrower than
  // what the page renders) would otherwise blank everything it omits.
  const getSection = useCallback((key, fallback) => {
    const section = pageData?.sections?.find((s) => s.section_key === key);
    if (!section?.content) return fallback;
    return { ...fallback, ...section.content };
  }, [pageData]);

  // Hero section content from API with fallbacks
  const heroContent = useMemo(() => getSection("hero", {
    badge_text: "Welcome to Our Community",
    heading: "Empowering",
    heading_accent: "Business Growth",
    subtitle: "Connecting businesses, fostering opportunities, and driving economic success within our Dawoodi Bohra community in Chicago.",
  }), [getSection]);

  // About section content from API with fallbacks
  const aboutContent = useMemo(() => getSection("about", {
    heading: "About Us",
    body: "Al-Tijarat Raabehah Chicago is a platform for promoting businesses and economic empowerment within our Jamaat. We facilitate networking, visibility, and growth opportunities through events, a business directory, and consulting services.",
  }), [getSection]);

  // CTA section content from API with fallbacks
  const ctaContent = useMemo(() => getSection("cta", {
    heading: "Need Business Guidance?",
    body: "Our expert consultants are here to help you grow your business with personalized advice and support tailored to your unique needs.",
    button_text: "Schedule a Consultation",
    button_link: "https://calendar.app.google/aA6HSupCUZrrszhd7",
  }), [getSection]);

  // Stats icons mapping (icons can't be stored in DB)
  const statsIcons = [
    <HiOutlineOfficeBuilding className="w-8 h-8" />,
    <HiOutlineCalendar className="w-8 h-8" />,
    <HiOutlineUsers className="w-8 h-8" />,
    <HiOutlineSupport className="w-8 h-8" />,
  ];

  // Memoized static data with API overrides
  const statsContent = useMemo(() => getSection("stats", {
    items: [
      { number: "100+", label: "Businesses" },
      { number: "50+", label: "Events" },
      { number: "1K+", label: "Members" },
      { number: "24/7", label: "Support" },
    ],
  }), [getSection]);

  const stats = useMemo(
    () =>
      (statsContent.items || []).map((item, index) => ({
        ...item,
        icon: statsIcons[index] || <HiOutlineOfficeBuilding className="w-8 h-8" />,
      })),
    [statsContent]
  );

  const eventsIcons = [
    <HiOutlineUserGroup className="w-8 h-8" />,
    <HiOutlineDesktopComputer className="w-8 h-8" />,
  ];

  // NOTE: this key does not match any row in page_contents — the stored
  // section is keyed "upcoming_events" — so the fallback below always renders
  // and admin edits to that section have no effect. That is deliberate for
  // now: the stored row holds only { heading, description }, both carrying
  // outdated copy ("Upcoming Events", no year and no items), so switching the
  // key would revert the heading and drop the event cards.
  //
  // To make this section admin-editable: update the stored row to the 2026
  // wording, extend its schema with an `items` array, then change this key to
  // "upcoming_events". getSection now merges per-field, so a partial row will
  // no longer blank the section.
  const eventsContent = useMemo(() => getSection("events", {
    heading: "2026 Upcoming Events",
    subtitle: "Watch out for these upcoming events in 2026.",
    items: [
      {
        title: "Biz Connect Chicago",
        // Keep in step with BizConnectShowcase.jsx — the event is two days.
        description:
          "A two-day Business Expo presented by Anjuman-e-Saifee Chicago — keynotes, a full exhibition floor and deliberate networking. Registration is open.",
        date: "5–6 December 2026",
        location: "Compass Arena, Willowbrook, IL",
        href: "/bizconnectchicago",
      },
    ],
  }), [getSection]);

  const upcomingEvents = useMemo(
    () =>
      (eventsContent.items || []).map((item, index) => ({
        ...item,
        icon: eventsIcons[index] || <HiOutlineUserGroup className="w-8 h-8" />,
      })),
    [eventsContent]
  );

  const missionIcons = [
    <HiOutlineTrendingUp className="w-12 h-12" />,
    <HiOutlineUserGroup className="w-12 h-12" />,
    <HiOutlineCurrencyDollar className="w-12 h-12" />,
  ];

  const missionContent = useMemo(() => getSection("mission", {
    items: [
      {
        title: "Business Growth",
        description: "Empowering local businesses with resources, networking, and growth opportunities.",
      },
      {
        title: "Community Connection",
        description: "Fostering meaningful relationships and collaborations within our community.",
      },
      {
        title: "Economic Empowerment",
        description: "Driving economic success and prosperity for all community members.",
      },
    ],
  }), [getSection]);

  const missionCards = useMemo(
    () =>
      (missionContent.items || []).map((item, index) => ({
        ...item,
        icon: missionIcons[index] || <HiOutlineTrendingUp className="w-12 h-12" />,
      })),
    [missionContent]
  );

  const fetchBusinesses = useCallback(() => {
    getSponserBusinesses({ limit: 3 });
  }, [getSponserBusinesses]);

  useEffect(() => {
    fetchBusinesses();
  }, [fetchBusinesses]);

  useEffect(() => {
    if (isSuccess && data?.businesses) {
      setSponserBusinesses(data.businesses);
    }
  }, [isSuccess, data]);

  // Loading skeleton component
  const BusinessSkeleton = () => (
    <div className="bg-white p-6 rounded-xl shadow-md animate-pulse">
      <div className="h-6 bg-gray-200 rounded mb-4 w-24"></div>
      <div className="h-8 bg-gray-200 rounded mb-2 w-3/4"></div>
      <div className="h-4 bg-gray-200 rounded mb-1 w-1/2"></div>
      <div className="h-4 bg-gray-200 rounded mb-4 w-1/3"></div>
      <div className="h-16 bg-gray-200 rounded mb-4"></div>
      <div className="h-6 bg-gray-200 rounded w-20"></div>
    </div>
  );

  return (
    <main className="min-h-screen bg-white" role="main">
      <SEO />
      {/* Enhanced Hero Section */}
      <section
        className="relative min-h-screen w-full overflow-hidden bg-gradient-to-br from-[#135879] via-[#1a6b8f] to-[#135879]"
        aria-labelledby="hero-heading"
      >
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
          {/* Animated gradient orbs */}
          <div className="absolute -top-1/4 -right-1/4 w-[800px] h-[800px] bg-gradient-radial from-[#f8d56b]/20 via-transparent to-transparent rounded-full animate-pulse opacity-60"></div>
          <div
            className="absolute -bottom-1/4 -left-1/4 w-[600px] h-[600px] bg-gradient-radial from-[#f8d56b]/15 via-transparent to-transparent rounded-full animate-pulse opacity-40"
            style={{ animationDelay: "1s" }}
          ></div>

          {/* Floating particles */}
          <div
            className="absolute top-1/4 left-1/4 w-2 h-2 bg-[#f8d56b]/30 rounded-full animate-bounce"
            style={{ animationDelay: "0.5s" }}
          ></div>
          <div
            className="absolute top-3/4 right-1/3 w-1 h-1 bg-white/40 rounded-full animate-ping"
            style={{ animationDelay: "1s" }}
          ></div>
          <div
            className="absolute top-1/2 right-1/4 w-1.5 h-1.5 bg-[#f8d56b]/40 rounded-full animate-pulse"
            style={{ animationDelay: "2s" }}
          ></div>

          {/* Subtle grid pattern */}
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCIgdmlld0JveD0iMCAwIDYwIDYwIj4KPHBhdGggZD0iTTYwIDBIMHY2MCIgZmlsbD0ibm9uZSIgc3Ryb2klPSIjZmZmZmZmMDIiIHN0cm9rZS13aWR0aD0iMC41Ii8+CjxwYXRoIGQ9Ik0wIDBoNjAiIGZpbGw9Im5vbmUiIHN0cm9rZT0iI2ZmZmZmZjAyIiBzdHJva2Utd2lkdGg9IjAuNSIvPgo8L3N2Zz4=')] opacity-[0.03]"></div>
        </div>

        <div className="relative z-10  pt-20 flex items-center justify-center h-full md:px-4 px-2 md:lg:px-8">
          <div className="text-center max-w-5xl mx-auto">
            <div className="inline-flex items-center md:px-4 md:py-2  p-1 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 mb-6 animate-fade-in">
              <span className="h-2 w-2 rounded-full bg-[#f8d56b] mr-2"></span>
              <span className="text-sm font-medium text-white">
                {heroContent.badge_text}
              </span>
            </div>

            <h1 className="text-xl md:text-5xl font-bold text-white mb-6 leading-tight animate-fade-in-up">
              {heroContent.heading}{" "}
              <span className="text-transparent bg-clip-text bg-[#f9b208]">
                {heroContent.heading_accent}
              </span>
              <br />
              in Our Community
            </h1>

            <p className="text-base md:text-xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed animate-fade-in-up animate-delay-100">
              {heroContent.subtitle}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center animate-fade-in-up animate-delay-200">
              <Link
                to="/business-directory"
                className="relative group bg-[#f9b208] hover:bg-[#f8d56b] text-gray-900 font-semibold md:py-4 py-2 md:px-8 px-2 rounded-lg text-lg transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl hover:shadow-[#f8d56b44] min-w-[200px] text-center"
              >
                <span className="relative z-10">Explore Directory</span>
                <span className="absolute inset-0 bg-gradient-to-r from-[#f9b208] to-[#f8d56b] opacity-0 group-hover:opacity-100 rounded-lg transition-opacity duration-300"></span>
              </Link>
              <Link
                to="/about"
                className="relative group bg-transparent hover:bg-white/10 text-white font-semibold md:py-4 py-2 md:px-8 px-2 rounded-lg border-2 border-white/30 hover:border-white/50 text-lg transition-all duration-300 transform hover:scale-105 min-w-[200px] text-center"
              >
                <span className="relative z-10">Learn More</span>
                <span className="absolute inset-0 bg-white/5 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity duration-300"></span>
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {stats.map((stat, index) => (
                <div
                  key={index}
                  className="text-center p-4 rounded-xl bg-white/5 backdrop-blur-sm border border-white/10 hover:bg-white/10 transition-all duration-300 transform hover:scale-105"
                >
                  <div
                    className="text-[#f8d56b] mb-3 flex justify-center"
                    aria-label={stat.label}
                  >
                    {stat.icon}
                  </div>
                  <div className="text-2xl sm:text-3xl md:text-4xl font-bold bg-gradient-to-r from-[#f8d56b] to-[#f9b208] bg-clip-text text-transparent mb-2">
                    {stat.number}
                  </div>
                  <div className="text-sm text-gray-200 uppercase tracking-wider font-medium">
                    {stat.label}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section
        className="py-20 px-4 max-w-7xl mx-auto"
        aria-labelledby="about-heading"
      >
        <div className="text-center mb-16">
          <h2
            id="about-heading"
            className="text-4xl md:text-5xl font-bold text-[#135879] mb-6"
          >
            {aboutContent.heading}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#d4af37] to-[#f9b208] mx-auto mb-8 rounded-full"></div>
          <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed">
            {aboutContent.body}
          </p>
        </div>

        {/* Mission Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-16">
          {missionCards.map((card, index) => (
            <div
              key={index}
              className="group text-center p-8 rounded-2xl bg-gradient-to-br from-white to-gray-50 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-gray-100"
            >
              <div
                className="text-[#135879] mb-6 flex justify-center group-hover:scale-110 transition-transform duration-300"
                aria-label={card.title}
              >
                {card.icon}
              </div>
              <h3 className="text-xl font-bold text-[#135879] mb-4">
                {card.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {card.description}
              </p>
            </div>
          ))}
        </div>
      </section>

      <section
        className="py-20 bg-gradient-to-br from-gray-50 to-white"
        aria-labelledby="featured-companies-heading"
      >
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <h2
              id="featured-companies-heading"
              className="text-4xl md:text-5xl font-bold text-[#135879] mb-6"
            >
              Featured Companies
            </h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#d4af37] to-[#f9b208] mx-auto mb-8 rounded-full"></div>
            <p className="text-lg md:text-xl text-gray-700 max-w-4xl mx-auto mb-8 leading-relaxed">
              Discover and connect with businesses in our community
            </p>
          </div>

          {/* Error State */}
          {isError && (
            <div className="text-center py-12">
              <div className="text-red-500 text-lg mb-4">
                ⚠️ Unable to load featured companies
              </div>
              <button
                onClick={fetchBusinesses}
                className="bg-[#135879] hover:bg-[#0e4562] text-white font-semibold py-2 px-6 rounded-lg transition duration-300"
              >
                Try Again
              </button>
            </div>
          )}

          {/* Loading State */}
          {isLoading && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
              {[...Array(3)].map((_, index) => (
                <BusinessSkeleton key={index} />
              ))}
            </div>
          )}

          {/* Business Cards */}
          {!isLoading &&
            !isError &&
            sponserBusinesses &&
            sponserBusinesses.length > 0 && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
                {sponserBusinesses.map((business, index) => (
                  <article
                    key={business.id || index}
                    className="group bg-white p-6 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
                  >
                    <div className="mb-4">
                      {business.level2_sponsored && (
                        <span className="inline-flex items-center gap-1 bg-purple-600 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md border border-purple-500">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Premium Sponsor
                        </span>
                      )}
                      {business.sponsored && !business.level2_sponsored && (
                        <span className="inline-flex items-center gap-1 bg-yellow-500 text-white text-xs font-bold px-3 py-1 rounded-full shadow-md border border-yellow-400">
                          <svg
                            className="w-3 h-3"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                          </svg>
                          Sponsored
                        </span>
                      )}
                    </div>

                    <header className="mb-4">
                      <a
                        href={
                          business.website?.startsWith("http")
                            ? business.website
                            : `https://${business.website}`
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-xl font-bold text-[#135879] hover:text-[#0e4562] transition-colors duration-200 group-hover:underline"
                        aria-label={`Visit ${business.company_name} website`}
                      >
                        {business.company_name}
                      </a>
                      <div className="mt-2 space-y-1">
                        <p className="text-gray-700 font-medium">
                          {business.name}
                        </p>
                        <p className="text-gray-600 text-sm">
                          {business.designation}
                        </p>
                      </div>
                    </header>

                    <p className="text-gray-600 mb-4 leading-relaxed line-clamp-3">
                      {business.description}
                    </p>

                    <footer>
                      <span className="inline-block bg-gradient-to-r from-gray-100 to-gray-200 text-gray-700 text-sm font-medium px-3 py-1 rounded-full">
                        {business.industry}
                      </span>
                    </footer>
                  </article>
                ))}
              </div>
            )}

          {/* Empty State */}
          {!isLoading &&
            !isError &&
            (!sponserBusinesses || sponserBusinesses.length === 0) && (
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg mb-4">
                  🏢 No featured companies available
                </div>
                <p className="text-gray-600">
                  Check back later for featured businesses in our community.
                </p>
              </div>
            )}

          <div className="text-center">
            <Link
              to="/business-directory"
              className="group inline-flex items-center gap-2 bg-[#135879] hover:bg-[#0e4562] text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#135879]/30"
              aria-label="View complete business directory"
            >
              <span>View Full Directory</span>
              <svg
                className="w-5 h-5 group-hover:translate-x-1 transition-transform"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            </Link>
          </div>
        </div>
      </section>

      <section
        className="py-20 max-w-7xl mx-auto px-4"
        aria-labelledby="events-heading"
      >
        <div className="text-center mb-16">
          <h2
            id="events-heading"
            className="text-4xl md:text-5xl font-bold text-[#135879] mb-6"
          >
            {eventsContent.heading || "2026 Upcoming Events"}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#d4af37] to-[#f9b208] mx-auto mb-8 rounded-full"></div>
          <p className="text-lg md:text-xl text-gray-600 mb-8 max-w-4xl mx-auto leading-relaxed">
            {eventsContent.subtitle || "Watch out for these upcoming events in 2026."}
          </p>
        </div>

        <div
          className={`grid grid-cols-1 gap-8 mb-12 ${
            upcomingEvents.length > 1 ? "md:grid-cols-2" : "max-w-2xl mx-auto"
          }`}
        >
          {upcomingEvents.map((event, index) => {
            // Cards with an href become links to their event page; the rest
            // stay plain articles, as they have nowhere to go.
            const Card = event.href ? Link : "article";
            const cardProps = event.href
              ? { to: event.href, "aria-label": `${event.title} — view event details` }
              : {};

            return (
            <Card
              key={index}
              {...cardProps}
              className="group block bg-white p-8 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100"
            >
              <div className="flex items-center mb-4">
                <div className="text-[#d4af37] mr-4" aria-label={event.title}>
                  {event.icon}
                </div>
                <h3 className="text-2xl font-bold text-[#d4af37] group-hover:text-[#c19b2e] transition-colors">
                  {event.title}
                </h3>
              </div>

              <p className="text-gray-600 mb-6 leading-relaxed">
                {event.description}
              </p>

              <div className="flex items-center text-sm text-gray-500 flex-wrap gap-y-2">
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="mr-4">{event.date}</span>
                <svg
                  className="w-4 h-4 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
                <span>{event.location}</span>
              </div>

              {event.href && (
                <span className="mt-6 inline-flex items-center gap-2 text-[#135879] font-semibold group-hover:gap-3 transition-all">
                  View event details
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M14 5l7 7m0 0l-7 7m7-7H3"
                    />
                  </svg>
                </span>
              )}
            </Card>
            );
          })}
        </div>

        <div className="text-center">
          <Link
            to="/events"
            className="group inline-flex items-center gap-2 bg-[#135879] hover:bg-[#0e4562] text-white font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl focus:outline-none focus:ring-4 focus:ring-[#135879]/30"
            aria-label="View all upcoming events"
          >
            <span>View All Events</span>
            <svg
              className="w-5 h-5 group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 7l5 5m0 0l-5 5m5-5H6"
              />
            </svg>
          </Link>
        </div>
      </section>

      <section className="py-20 px-4" aria-labelledby="consultation-heading">
        <div className="max-w-4xl mx-auto text-center bg-gradient-to-br from-[#135879] to-[#0e4562] rounded-3xl p-12 shadow-2xl">
          <div
            className="text-[#d4af37] mb-8 flex justify-center"
            aria-label="Business consultation"
          >
            <HiOutlineBriefcase className="w-16 h-16" />
          </div>
          <h2
            id="consultation-heading"
            className="text-3xl md:text-4xl font-bold text-white mb-6"
          >
            {ctaContent.heading}
          </h2>
          <p className="text-lg md:text-xl text-gray-200 mb-10 max-w-3xl mx-auto leading-relaxed">
            {ctaContent.body}
          </p>
          <a
            target="_blank"
            href={ctaContent.button_link || "https://calendar.app.google/aA6HSupCUZrrszhd7"}
            className="group inline-flex items-center gap-2 bg-[#d4af37] hover:bg-[#c19b2e] text-black font-semibold py-4 px-8 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-[#d4af37]/50"
            aria-label="Schedule a business consultation"
          >
            <span>{ctaContent.button_text || "Schedule a Consultation"}</span>
            <svg
              className="w-5 h-5 group-hover:translate-x-1 transition-transform"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
          </a>
        </div>
      </section>
    </main>
  );
};

export default Home;
